import { UserWithActiveStatusResponse, createUser, updateUser, getUserPermissions, getUserMenus } from "../../services/userService";
import { useToast } from "../../hooks/useToast";
import { useTranslation } from 'react-i18next';
import React, { useCallback } from "react";
import { LocationResponse, getLocations } from "../../services/locationService";
import { Actions, Box, Button, Column, Columns, Drawer, Dropdown, Stack, TextField, Text, Checkbox, Divider } from "braid-design-system";
import { getAllUserGroups, UserGroupsResponse } from "../../services/authService";
import { SystemPermissionResponse, getSystemPermissions } from "../../services/systemPermissionService";
import { SystemMenuResponse, getAllSystemMenus } from "../../services/systemMenuService";
import { ItReportTemplateResponse, getAllReportTemplates, getUserReports } from "../../services/reportService";
import Loading from "../Loading";
import { schemas } from '../../types/zod-api-schema';
import { groupPermissionsByCategory, sortCategories, getCategoryPermissionIds } from "../../utils/permissionGrouper";
import { groupReportsByCategory, sortReportCategories, getCategoryReportIds } from "../../utils/reportGrouper";
import { useUserPermissionAndMenu } from "../../contexts/UserPermissionAndMenuProvider";
import NumericTextField from "../NumericTextField";
import { sortByOrderNum } from "../../utils/menuSorter";

interface FormErrors {
    name?: string;
    user_name?: string;
    active_branch_id?: string;
    user_group_id?: string;
    ref_number?: string;
    user_role?: string
}

interface CreateUpdateUserDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    userToEdit?: UserWithActiveStatusResponse;
    onUserCreated?: () => void;
}

const CreateUpdateUserDrawer: React.FC<CreateUpdateUserDrawerProps> = ({
    isOpen,
    onClose,
    userToEdit,
    onUserCreated,
}) => {
    const [locations, setLocations] = React.useState<LocationResponse[]>([]);
    const [userName, setUserName] = React.useState<string | null>(userToEdit ? userToEdit.user_name : "");
    const [name, setName] = React.useState<string | null>(userToEdit ? userToEdit.name : "");
    const [refNumber, setRefNumber] = React.useState<string | null>("");
    const [userRole, setUserRole] = React.useState<string>("CLERK");
    const [activeBranchId, setActiveBranchId] = React.useState<string | null>(userToEdit ? String(userToEdit.active_branch_id ?? "") : null);
    const [groups, setGroups] = React.useState<Array<UserGroupsResponse>>([]);
    const [userGroupId, setUserGroupId] = React.useState<number | null>(userToEdit ? userToEdit.user_group_id : null);
    const [systemPermissions, setSystemPermissions] = React.useState<Array<SystemPermissionResponse>>([]);
    const [selectedPermissions, setSelectedPermissions] = React.useState<Set<number>>(new Set());
    const [systemMenus, setSystemMenus] = React.useState<Array<SystemMenuResponse>>([]);
    const [selectedMenus, setSelectedMenus] = React.useState<Set<number>>(new Set());
    const [reportTemplates, setReportTemplates] = React.useState<Array<ItReportTemplateResponse>>([]);
    const [selectedReports, setSelectedReports] = React.useState<Set<number>>(new Set());
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const toast = useToast();
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(t('page.user_management.drawer.loading.fetching', { defaultValue: 'Fetching required data...' }));
    const { hasPermission, setRefreshTrigger } = useUserPermissionAndMenu();

    const fetchLocations = useCallback(async () => {
        try {
            setLoading(true);
            const locationsData = await getLocations();
            setLocations(locationsData);
        } catch (error: any) {
            toast.showError(
                t('page.user_management.drawer.error.fetch_branches', { defaultValue: 'Error fetching branches: {{message}}', message: error?.message || 'Unknown error' })
            );
        }
        finally {
            setLoading(false);
        }
    }, [setLoading, toast]);

    const fetchUserGroups = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllUserGroups()
            setGroups(response);
        } catch (error: any) {
            toast.showError(
                t('page.user_management.drawer.error.fetch_groups', { defaultValue: 'Error fetching user groups: {{message}}', message: error?.message || 'Unknown error' })
            );
        }
        finally {
            setLoading(false);
        }
    }, [setLoading, toast]);

    const fetchSystemPermissions = useCallback(async () => {
        try {
            setLoading(true);
            const permissions = await getSystemPermissions();
            setSystemPermissions(permissions);
        } catch (error: any) {
            toast.showError(
                t('page.user_management.drawer.error.fetch_permissions', { defaultValue: 'Error fetching permissions: {{message}}', message: error?.message || 'Unknown error' })
            );
        }
        finally {
            setLoading(false);
        }
    }, [setLoading, toast]);

    const fetchSystemMenus = useCallback(async () => {
        try {
            setLoading(true);
            const menus = await getAllSystemMenus();
            setSystemMenus(menus);
        } catch (error: any) {
            toast.showError(
                t('page.user_management.drawer.error.fetch_menus', { defaultValue: 'Error fetching system menus: {{message}}', message: error?.message || 'Unknown error' })
            );
        }
        finally {
            setLoading(false);
        }
    }, [setLoading, toast]);

    const fetchReportTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const reports = await getAllReportTemplates();
            setReportTemplates(reports);
        } catch (error: any) {
            toast.showError(
                t('page.user_management.drawer.error.fetch_reports', { defaultValue: 'Error fetching report templates: {{message}}', message: error?.message || 'Unknown error' })
            );
        }
        finally {
            setLoading(false);
        }
    }, [setLoading, toast]);

    React.useEffect(() => {
        // Fetch locations, groups, permissions, menus and reports when the drawer is opened
        if (isOpen) {
            fetchLocations();
            fetchUserGroups();
            fetchSystemPermissions();
            fetchSystemMenus();
            fetchReportTemplates();
        }
    }, [isOpen, fetchLocations, fetchSystemPermissions, fetchSystemMenus, fetchUserGroups, fetchReportTemplates]);

    React.useEffect(() => {
        // Load user permissions, menus, and reports when editing a user
        if (userToEdit && isOpen) {
            // Populate all form fields with user data
            setUserName(userToEdit.user_name || "");
            setName(userToEdit.name || "");
            setRefNumber((userToEdit as any).ref_number || "");
            setUserRole((userToEdit as any).user_role || "CLERK");
            setActiveBranchId(userToEdit.active_branch_id != null ? String(userToEdit.active_branch_id) : null);
            setUserGroupId(userToEdit.user_group_id);

            // Load permissions, menus, and reports
            Promise.all([
                getUserPermissions(userToEdit.id),
                getUserMenus(userToEdit.id),
                getUserReports(userToEdit.id)
            ]).then(([permissions, menus, reports]) => {
                setSelectedPermissions(new Set(permissions));
                setSelectedMenus(new Set(menus));
                setSelectedReports(new Set(reports));
            }).catch((error) => {
                toast.showError(
                    t('page.user_management.drawer.error.load_user', { defaultValue: 'Error loading user data: {{message}}', message: error?.message || 'Unknown error' })
                );
            });
        } else if (!userToEdit && isOpen) {
            // Reset form for creating new user
            setUserName("");
            setName("");
            setRefNumber("");
            setUserRole("CLERK");
            setActiveBranchId(null);
            setUserGroupId(null);
            setSelectedPermissions(new Set());
            setSelectedMenus(new Set());
            setSelectedReports(new Set());
        }
    }, [userToEdit, isOpen, toast]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrors({});

        try {
            setLoading(true);
            setLoadingMessage(userToEdit ? t('page.user_management.drawer.loading.updating', { defaultValue: 'Updating user...' }) : t('page.user_management.drawer.loading.creating', { defaultValue: 'Creating user...' }));
            setSubmitting(true);

            // Prepare form data
            const userData = {
                name: name,
                user_name: userName,
                ref_number: refNumber || null,
                active_branch_id: activeBranchId,
                user_group_id: userGroupId,
                user_role: userRole || 'CLERK',
                system_permissions: Array.from(selectedPermissions),
                system_menus: Array.from(selectedMenus),
                user_reports: Array.from(selectedReports),
            };

            // Validate using Zod schema from generated API schemas
            // The UserCreateRequest schema from zod-api-schema will validate the data
            const validationResult = schemas.UserCreateRequest.safeParse(userData);

            if (!validationResult.success) {
                // Extract and display Zod errors
                const zodErrors: FormErrors = {};
                validationResult.error.issues.forEach((issue) => {
                    const field = issue.path[0] as string;
                    zodErrors[field as keyof FormErrors] = issue.message;
                });
                setErrors(zodErrors);
                toast.showError(t('page.user_management.drawer.validation.fix_errors', { defaultValue: 'Please fix validation errors' }));
                setSubmitting(false);
                return;
            }

            // Call API to create or update user
            if (userToEdit) {
                await updateUser(userToEdit.id, userData as any);
                toast.showSuccess(t('page.user_management.drawer.success.updated', { defaultValue: 'User updated successfully!' }));
            } else {
                await createUser(userData as any);
                toast.showSuccess(t('page.user_management.drawer.success.created', { defaultValue: 'User created successfully!' }));
            }

            setUserName("");
            setName("");
            setRefNumber("");
            setActiveBranchId(null);
            setUserGroupId(null);
            setUserRole("CLERK");
            setSelectedPermissions(new Set());
            setSelectedMenus(new Set());
            setSelectedReports(new Set());

            if (onUserCreated) {
                onUserCreated();
            }

            onClose();
        } catch (error: any) {
            // Handle API errors
            if (typeof error === 'string') {
                toast.showError(error);
            } else {
                toast.showError(error?.message || (userToEdit ? t('page.user_management.drawer.error.update_failed', { defaultValue: 'Failed to update user' }) : t('page.user_management.drawer.error.create_failed', { defaultValue: 'Failed to create user' })));
            }
        } finally {
            setSubmitting(false);
            setLoading(false);
            setRefreshTrigger(true);
        }
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={userToEdit ? t('page.user_management.drawer.title.update', { defaultValue: 'Update User {{name}}', name: userToEdit.name }) : t('page.user_management.drawer.title.create', { defaultValue: 'Create New User' })}
            width="medium"
        >
            {
                loading && <Loading message={`${t('page.user_management.drawer.loading.please_wait', { defaultValue: 'Please wait' })}, ${loadingMessage}`} />
            }
            <form onSubmit={handleSubmit} autoComplete="off">
                {/* Drawer content goes here */}
                <Box padding="large">
                    <Stack space="large">
                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Stack space="small">
                                    <TextField
                                        label={t('page.user_management.drawer.label.full_name', { defaultValue: 'Full Name*' })}
                                        value={name || ""}
                                        placeholder={t('page.user_management.drawer.placeholder.full_name', { defaultValue: 'Enter User Full Name' })}
                                        tone={errors.name ? "critical" : undefined}
                                        message={errors.name}
                                        onChange={(e) => {
                                            setName(e.currentTarget.value);
                                            if (errors.name) setErrors({ ...errors, name: undefined });
                                        }}
                                    />
                                    <TextField
                                        label={t('page.user_management.drawer.label.username', { defaultValue: 'User Name*' })}
                                        placeholder={t('page.user_management.drawer.placeholder.username', { defaultValue: 'Enter username' })}
                                        value={userName || ""}
                                        tone={errors.user_name ? "critical" : undefined}
                                        message={errors.user_name}
                                        onChange={(e) => {
                                            setUserName(e.currentTarget.value);
                                            if (errors.user_name) setErrors({ ...errors, user_name: undefined });
                                        }}
                                    />
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Stack space="small">
                                    <Box>
                                        <Dropdown
                                            label={t('page.user_management.drawer.dropdown.active_branch', { defaultValue: 'Active Branch*' })}
                                            placeholder={t('page.user_management.drawer.placeholder.active_branch', { defaultValue: 'Select User Branch' })}
                                            value={activeBranchId ?? ""}
                                            onChange={(e) => {
                                                setActiveBranchId(e.currentTarget.value || null);
                                                if (errors.active_branch_id) setErrors({ ...errors, active_branch_id: undefined });
                                            }}
                                            tone={errors.active_branch_id ? "critical" : undefined}
                                            message={errors.active_branch_id}
                                        >
                                                {locations.map((location) => (
                                                    <option key={String(location.loc_code ?? "")} value={String(location.loc_code ?? "")}>
                                                        {String(location.loc_desc ?? location.loc_code ?? "")}
                                                    </option>
                                                ))}
                                        </Dropdown>
                                        {errors.active_branch_id && <Text tone="critical" size="small">{errors.active_branch_id}</Text>}
                                    </Box>
                                    <Box>
                                        <Dropdown
                                            label={t('page.user_management.drawer.dropdown.user_group', { defaultValue: 'User Group' })}
                                            placeholder={t('page.user_management.drawer.placeholder.user_group', { defaultValue: 'Select User Group' })}
                                            value={userGroupId ? userGroupId.toString() : ""}
                                            onChange={(e) => {
                                                setUserGroupId(Number(e.currentTarget.value));
                                                if (errors.user_group_id) setErrors({ ...errors, user_group_id: undefined });
                                            }}
                                            tone={errors.user_group_id ? "critical" : undefined}
                                            message={errors.user_group_id}
                                            disabled={true}
                                        >
                                            {groups.map((group) => (
                                                <option key={group.id} value={String(group.id)}>
                                                    {group.name}
                                                </option>
                                            ))}
                                        </Dropdown>
                                        {errors.user_group_id && <Text tone="critical" size="small">{errors.user_group_id}</Text>}
                                    </Box>
                                </Stack>
                            </Column>
                        </Columns>
                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <NumericTextField
                                    label={t('page.user_management.drawer.label.reference', { defaultValue: 'Reference Number' })}
                                    placeholder={t('page.user_management.drawer.placeholder.reference', { defaultValue: 'Enter reference number' })}
                                    value={refNumber || ""}
                                    mode="integer"
                                    onChange={(value) => {
                                        setRefNumber(value);
                                    }}
                                    onInvalid={() =>
                                        setErrors((prev) => ({
                                            ...prev,
                                            ref_number: t('page.user_management.drawer.validation.numbers_only', {
                                                defaultValue: 'Please enter numbers only',
                                            }),
                                        }))
                                    }
                                    tone={errors.ref_number ? "critical" : undefined}
                                    message={errors.ref_number}
                                />
                            </Column>
                            <Column width="1/2">
                                <Dropdown
                                    label={t('page.user_management.drawer.label.user_role', { defaultValue: 'User Role' })}
                                    placeholder={t('page.user_management.drawer.placeholder.user_role', { defaultValue: 'Select User Role' })}
                                    value={userRole || "CLERK"}
                                    onChange={(e) => {
                                        setUserRole(e.currentTarget.value);
                                    }}
                                    tone={errors.user_role ? "critical" : undefined}
                                    message={errors.user_role}
                                >
                                    <option value="ADMIN">Admin</option>
                                    <option value="CLERK">Clerk</option>
                                    <option value="CASHIER">Cashier</option>
                                    <option value="SALES_REP">Sales Rep</option>
                                    <option value="CUSTOMER">Customer</option>
                                    <option value="RIDER">Delivery Rider</option>
                                </Dropdown>
                            </Column>
                        </Columns>
                        <Divider />
                        <Stack space="small">
                            <Columns space="small" alignY="center">
                                <Column>
                                    <Text tone="info" weight="strong">System Permissions</Text>
                                </Column>
                                <Column width="content">
                                    <Button
                                        size="small"
                                        variant="ghost"
                                        tone="brandAccent"
                                        onClick={() => {
                                            const allPermissionIds = systemPermissions.map(p => p.id || 0);
                                            setSelectedPermissions(new Set(allPermissionIds));
                                        }}
                                    >
                                        {t('page.user_management.drawer.button.select_all', { defaultValue: 'Select All' })}
                                    </Button>
                                </Column>
                                <Column width="content">
                                    <Button
                                        size="small"
                                        variant="ghost"
                                        tone="neutral"
                                        onClick={() => {
                                            setSelectedPermissions(new Set());
                                        }}
                                    >
                                        {t('page.user_management.drawer.button.clear_all', { defaultValue: 'Clear All' })}
                                    </Button>
                                </Column>
                            </Columns>
                            <Stack space="medium">
                                {(() => {
                                    const groupedPerms = groupPermissionsByCategory(systemPermissions);
                                    const sortedCategories = sortCategories(Object.keys(groupedPerms));

                                    return sortedCategories.map((category) => {
                                        const categoryPermissions = groupedPerms[category];
                                        const categoryPermIds = getCategoryPermissionIds(categoryPermissions);

                                        return (
                                            <Stack key={category} space="small">
                                                <Columns space="small" alignY="center">
                                                    <Column>
                                                        <Text weight="strong" tone="neutral" size="small">
                                                            {category}
                                                        </Text>
                                                    </Column>
                                                    <Column width="content">
                                                        <Button
                                                            size="small"
                                                            variant="ghost"
                                                            tone="brandAccent"
                                                            onClick={() => {
                                                                const newPermissions = new Set(selectedPermissions);
                                                                categoryPermIds.forEach(id => newPermissions.add(id));
                                                                setSelectedPermissions(newPermissions);
                                                            }}
                                                        >
                                                            {t('page.user_management.drawer.button.select', { defaultValue: 'Select' })}
                                                        </Button>
                                                    </Column>
                                                    <Column width="content">
                                                        <Button
                                                            size="small"
                                                            variant="ghost"
                                                            tone="neutral"
                                                            onClick={() => {
                                                                const newPermissions = new Set(selectedPermissions);
                                                                categoryPermIds.forEach(id => newPermissions.delete(id));
                                                                setSelectedPermissions(newPermissions);
                                                            }}
                                                        >
                                                            {t('page.user_management.drawer.button.deselect', { defaultValue: 'Deselect' })}
                                                        </Button>
                                                    </Column>
                                                </Columns>
                                                <Box paddingLeft="medium">
                                                    <Stack space="xsmall">
                                                        {categoryPermissions.map((permission) => (
                                                            <Checkbox
                                                                key={permission.id}
                                                                label={String(permission.name)}
                                                                checked={selectedPermissions.has(permission.id || 0)}
                                                                onChange={() => {
                                                                    const newPermissions = new Set(selectedPermissions);
                                                                    if (newPermissions.has(permission.id || 0)) {
                                                                        newPermissions.delete(permission.id || 0);
                                                                    } else {
                                                                        newPermissions.add(permission.id || 0);
                                                                    }
                                                                    setSelectedPermissions(newPermissions);
                                                                }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        );
                                    });
                                })()}
                            </Stack>
                        </Stack>
                        <Divider />
                        <Stack space="small">
                            <Columns space="small" alignY="center">
                                <Column>
                                    <Text weight="strong" tone="info">System Menus</Text>
                                </Column>
                                <Column width="content">
                                    <Button
                                        size="small"
                                        variant="ghost"
                                        tone="brandAccent"
                                        onClick={() => {
                                            const allMenuIds = systemMenus.map(m => m.id || 0);
                                            setSelectedMenus(new Set(allMenuIds));
                                        }}
                                    >
                                        {t('page.user_management.drawer.button.select_all', { defaultValue: 'Select All' })}
                                    </Button>
                                </Column>
                                <Column width="content">
                                    <Button
                                        size="small"
                                        variant="ghost"
                                        tone="neutral"
                                        onClick={() => {
                                            setSelectedMenus(new Set());
                                        }}
                                    >
                                        {t('page.user_management.drawer.button.clear_all', { defaultValue: 'Clear All' })}
                                    </Button>
                                </Column>
                            </Columns>
                            {systemMenus && systemMenus.length > 0 ? (
                                <Stack space="small">
                                    {(() => {
                                        // Get root menus (parent_id is null) sorted by dotted-numeric order
                                        const rootMenus = sortByOrderNum(
                                            systemMenus.filter(menu => menu.parent_id === null)
                                        );
                                        return rootMenus.map((menu) => {
                                            // Get children sorted by dotted-numeric order
                                            const childMenus = sortByOrderNum(
                                                systemMenus.filter(child => child.parent_id === menu.id)
                                            );
                                            return (
                                                <Stack key={menu.id} space="small">
                                                    <Checkbox
                                                        label={String(menu.label)}
                                                        checked={selectedMenus.has(menu.id || 0)}
                                                        onChange={() => {
                                                            const newMenus = new Set(selectedMenus);
                                                            const isCurrentlySelected = newMenus.has(menu.id || 0);

                                                            if (isCurrentlySelected) {
                                                                // Deselect parent and all children
                                                                newMenus.delete(menu.id || 0);
                                                                childMenus.forEach(child => {
                                                                    newMenus.delete(child.id || 0);
                                                                });
                                                            } else {
                                                                // Select parent and all children
                                                                newMenus.add(menu.id || 0);
                                                                childMenus.forEach(child => {
                                                                    newMenus.add(child.id || 0);
                                                                });
                                                            }
                                                            setSelectedMenus(newMenus);
                                                        }}
                                                    />
                                                    {/* Child menus of this parent */}
                                                    <Box paddingLeft="large">
                                                        <Stack space="small">
                                                            {childMenus.map((childMenu) => (
                                                                <Checkbox
                                                                    key={childMenu.id}
                                                                    label={String(childMenu.label)}
                                                                    checked={selectedMenus.has(childMenu.id || 0)}
                                                                    onChange={() => {
                                                                        const newMenus = new Set(selectedMenus);
                                                                        if (newMenus.has(childMenu.id || 0)) {
                                                                            newMenus.delete(childMenu.id || 0);
                                                                        } else {
                                                                            newMenus.add(childMenu.id || 0);
                                                                        }
                                                                        setSelectedMenus(newMenus);
                                                                    }}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                </Stack>
                                            );
                                        });
                                    })()}
                                </Stack>
                            ) : (
                                <Text>{t('page.user_management.drawer.empty.menus', { defaultValue: 'No menus available' })}</Text>
                            )}
                        </Stack>
                        {hasPermission("REPORT:VIEW") && (
                            <>
                                <Divider />
                                <Stack space="small">
                                    <Columns space="small" alignY="center">
                                        <Column>
                                            <Text weight="strong" tone="info">Reports</Text>
                                        </Column>
                                        <Column width="content">
                                            <Button
                                                size="small"
                                                variant="ghost"
                                                tone="brandAccent"
                                                onClick={() => {
                                                    const allReportIds = reportTemplates.map(r => r.id || 0);
                                                    setSelectedReports(new Set(allReportIds));
                                                }}
                                            >
                                                {t('page.user_management.drawer.button.select_all', { defaultValue: 'Select All' })}
                                            </Button>
                                        </Column>
                                        <Column width="content">
                                            <Button
                                                size="small"
                                                variant="ghost"
                                                tone="neutral"
                                                onClick={() => {
                                                    setSelectedReports(new Set());
                                                }}
                                            >
                                                {t('page.user_management.drawer.button.clear_all', { defaultValue: 'Clear All' })}
                                            </Button>
                                        </Column>
                                    </Columns>
                                    {reportTemplates && reportTemplates.length > 0 ? (
                                        <Stack space="medium">
                                            {(() => {
                                                const groupedReports = groupReportsByCategory(reportTemplates);
                                                const sortedCategories = sortReportCategories(Object.keys(groupedReports));

                                                return sortedCategories.map((category) => {
                                                    const categoryReports = groupedReports[category];
                                                    const categoryReportIds = getCategoryReportIds(categoryReports);

                                                    return (
                                                        <Stack key={category} space="small">
                                                            <Columns space="small" alignY="center">
                                                                <Column>
                                                                    <Text weight="strong" tone="neutral" size="small">
                                                                        {category}
                                                                    </Text>
                                                                </Column>
                                                                <Column width="content">
                                                                    <Button
                                                                        size="small"
                                                                        variant="ghost"
                                                                        tone="brandAccent"
                                                                        onClick={() => {
                                                                            const newReports = new Set(selectedReports);
                                                                            categoryReportIds.forEach(id => newReports.add(id));
                                                                            setSelectedReports(newReports);
                                                                        }}
                                                                    >
                                                                        Select
                                                                    </Button>
                                                                </Column>
                                                                <Column width="content">
                                                                    <Button
                                                                        size="small"
                                                                        variant="ghost"
                                                                        tone="neutral"
                                                                        onClick={() => {
                                                                            const newReports = new Set(selectedReports);
                                                                            categoryReportIds.forEach(id => newReports.delete(id));
                                                                            setSelectedReports(newReports);
                                                                        }}
                                                                    >
                                                                        Deselect
                                                                    </Button>
                                                                </Column>
                                                            </Columns>
                                                            <Box paddingLeft="medium">
                                                                <Stack space="xsmall">
                                                                    {categoryReports.map((report) => (
                                                                        <Checkbox
                                                                            key={report.id}
                                                                            label={String(report.name)}
                                                                            checked={selectedReports.has(report.id || 0)}
                                                                            onChange={() => {
                                                                                const newReports = new Set(selectedReports);
                                                                                if (newReports.has(report.id || 0)) {
                                                                                    newReports.delete(report.id || 0);
                                                                                } else {
                                                                                    newReports.add(report.id || 0);
                                                                                }
                                                                                setSelectedReports(newReports);
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Stack>
                                                            </Box>
                                                        </Stack>
                                                    );
                                                });
                                            })()}
                                        </Stack>
                                    ) : (
                                        <Text>{t('page.user_management.drawer.empty.reports', { defaultValue: 'No report templates available' })}</Text>
                                    )}
                                </Stack>
                            </>
                        )}
                        <Box display={"flex"} justifyContent={"flexEnd"}>
                            <Actions>
                                <Button
                                    type="submit"
                                    variant="ghost"
                                    tone="brandAccent"
                                >
                                    {submitting ? (userToEdit ? t('page.user_management.drawer.submit.updating', { defaultValue: 'Updating...' }) : t('page.user_management.drawer.submit.creating', { defaultValue: 'Creating...' })) : (userToEdit ? t('page.user_management.drawer.submit.update_user', { defaultValue: 'Update User' }) : t('page.user_management.drawer.submit.create_user', { defaultValue: 'Create User' }))}
                                </Button>
                            </Actions>
                        </Box>
                    </Stack>
                </Box>
            </form>
        </Drawer >
    )
}

export default CreateUpdateUserDrawer;
